import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { offerApi } from '../../lib/offerApi';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, User, DollarSign, Settings, Eye,
  Check, AlertCircle, Save, Send
} from 'lucide-react';

const STEPS = [
  { key: 'candidate', label: 'Candidate Details', icon: User },
  { key: 'compensation', label: 'Compensation', icon: DollarSign },
  { key: 'settings', label: 'Offer Settings', icon: Settings },
  { key: 'preview', label: 'Preview & Send', icon: Eye },
];

const DEPARTMENTS = ['Engineering', 'Product', 'Design', 'Marketing', 'Sales', 'Operations', 'Finance', 'Human Resources', 'Legal', 'Customer Success'];
const CATEGORIES = [
  { slug: 'food', label: 'Food & Meals', icon: '🍽' },
  { slug: 'transport', label: 'Transport', icon: '🚗' },
  { slug: 'health', label: 'Health & Wellness', icon: '🏥' },
  { slug: 'insurance', label: 'Insurance', icon: '🛡' },
  { slug: 'housing', label: 'Real Estate & Housing', icon: '🏠' },
  { slug: 'education', label: 'Education & Training', icon: '📚' },
  { slug: 'devices', label: 'Devices & Work Tools', icon: '💻' },
  { slug: 'lifestyle', label: 'Lifestyle & Entertainment', icon: '🎉' },
];

const NTA_BANDS = [
  { limit: 800000, rate: 0.00 },
  { limit: 2200000, rate: 0.15 },
  { limit: 3200000, rate: 0.18 },
  { limit: 3200000, rate: 0.21 },
  { limit: 3200000, rate: 0.24 },
  { limit: Infinity, rate: 0.24 },
];

function calcTax(grossMonthly, benefitsBudget, salaryStructure) {
  const g = grossMonthly || 0;
  const b = benefitsBudget || 0;
  const annualGross = g * 12;
  const annualBenefits = b * 12;
  const annualTaxable = annualGross - annualBenefits;
  const empPension = Math.round(annualTaxable * 0.08);
  const empPensionMo = Math.round(empPension / 12);
  const erPension = Math.round(annualGross * 0.10);
  const erPensionMo = Math.round(erPension / 12);
  const craFixed = Math.max(200000, annualGross * 0.01);
  const cra = craFixed + annualGross * 0.20;
  const chargeable = Math.max(0, annualTaxable - empPension - cra);
  let remaining = chargeable;
  let annualPaye = 0;
  for (const { limit, rate } of NTA_BANDS) {
    if (remaining <= 0) break;
    const t = Math.min(remaining, limit);
    annualPaye += Math.round(t * rate);
    remaining -= t;
  }
  const moPayeWith = Math.round(annualPaye / 12);
  const moTakeHome = g - b - empPensionMo - moPayeWith;

  // Without benefits
  const nbPension = Math.round(annualGross * 0.08);
  const nbCra = Math.max(200000, annualGross * 0.01) + annualGross * 0.20;
  const nbChargeable = Math.max(0, annualGross - nbPension - nbCra);
  let nbR = nbChargeable; let nbPaye = 0;
  for (const { limit, rate } of NTA_BANDS) {
    if (nbR <= 0) break;
    const t = Math.min(nbR, limit);
    nbPaye += Math.round(t * rate);
    nbR -= t;
  }
  const nbMoPaye = Math.round(nbPaye / 12);
  const nbMoPension = Math.round(nbPension / 12);
  const nbTakeHome = g - nbMoPension - nbMoPaye;

  return {
    monthly_paye: moPayeWith,
    monthly_take_home: moTakeHome,
    monthly_employee_pension: empPensionMo,
    monthly_employer_pension: erPensionMo,
    monthly_total_value: moTakeHome + b,
    tax_saved_monthly: nbMoPaye - moPayeWith,
    without: { paye: nbMoPaye, pension: nbMoPension, take_home: nbTakeHome },
    salary_breakdown: {
      basic: Math.round(g * (salaryStructure?.basic_pct || 40) / 100),
      housing: Math.round(g * (salaryStructure?.housing_pct || 20) / 100),
      transport: Math.round(g * (salaryStructure?.transport_pct || 10) / 100),
      other: Math.round(g * (salaryStructure?.other_pct || 30) / 100),
    }
  };
}

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function CreateOfferLetter() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    candidate_name: '', candidate_email: '', candidate_phone: '',
    position_title: '', department: '', reporting_to: '',
    start_date: '', employment_type: 'full_time', probation_months: 3,
    gross_monthly: 500000,
    salary_structure: { basic_pct: 40, housing_pct: 20, transport_pct: 10, other_pct: 30 },
    salary_preset: 'standard',
    benefits_budget: 85000,
    min_allocations: { health: 15000, insurance: 5000 },
    enabled_categories: ['food', 'transport', 'health', 'insurance', 'housing', 'education', 'devices', 'lifestyle'],
    selection_mode: 'candidate_chooses',
    annual_leave_days: 20, sick_leave_days: 10,
    work_arrangement: 'hybrid', notice_period: '1 month',
    offer_expiry_days: 7, signing_bonus: 0, relocation_support: 0,
    additional_notes: '',
  });

  const set = useCallback((key, val) => setForm(f => ({ ...f, [key]: val })), []);

  const tax = useMemo(() => calcTax(form.gross_monthly, form.benefits_budget, form.salary_structure), [form.gross_monthly, form.benefits_budget, form.salary_structure]);

  const canNext = () => {
    if (step === 0) return form.candidate_name && form.candidate_email && form.position_title && form.start_date;
    if (step === 1) return form.gross_monthly > 0 && form.benefits_budget >= 0;
    return true;
  };

  const handleSave = async (sendNow = false) => {
    setSaving(true);
    try {
      const payload = {
        candidate: {
          candidate_name: form.candidate_name,
          candidate_email: form.candidate_email,
          candidate_phone: form.candidate_phone,
          position_title: form.position_title,
          department: form.department,
          reporting_to: form.reporting_to,
          start_date: form.start_date,
          employment_type: form.employment_type,
          probation_months: form.probation_months,
        },
        compensation: {
          gross_monthly: form.gross_monthly,
          salary_structure: form.salary_structure,
          benefits_budget: form.benefits_budget,
          min_allocations: form.min_allocations,
          enabled_categories: form.enabled_categories,
        },
        settings: {
          selection_mode: form.selection_mode,
          annual_leave_days: form.annual_leave_days,
          sick_leave_days: form.sick_leave_days,
          work_arrangement: form.work_arrangement,
          notice_period: form.notice_period,
          offer_expiry_days: form.offer_expiry_days,
          signing_bonus: form.signing_bonus,
          relocation_support: form.relocation_support,
          additional_notes: form.additional_notes,
        },
      };
      const offer = await offerApi.create(payload);
      if (sendNow) {
        await offerApi.send(offer.id);
        toast.success('Offer created and sent to candidate!');
      } else {
        toast.success('Offer saved as draft');
      }
      navigate(`/company-admin/offer-letters/${offer.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create offer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="create-offer-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/company-admin/offer-letters')} className="p-2 rounded-lg hover:bg-stone-100" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5 text-[#636E72]" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-[#2D3436] font-[Manrope]">Create Offer Letter</h1>
          <p className="text-sm text-[#636E72]">Step {step + 1} of 4: {STEPS[step].label}</p>
        </div>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          const active = i === step;
          return (
            <React.Fragment key={s.key}>
              <button onClick={() => i <= step && setStep(i)} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${active ? 'bg-[#D64D7A] text-white shadow-sm' : done ? 'bg-pink-50 text-[#D64D7A]' : 'bg-stone-50 text-[#636E72]'}`} data-testid={`step-${s.key}`}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? 'bg-[#D64D7A]' : 'bg-stone-200'}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6 lg:p-8">
        {step === 0 && <Step1Candidate form={form} set={set} />}
        {step === 1 && <Step2Compensation form={form} set={set} tax={tax} />}
        {step === 2 && <Step3Settings form={form} set={set} />}
        {step === 3 && <Step4Preview form={form} tax={tax} />}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button onClick={() => step > 0 ? setStep(step - 1) : navigate('/company-admin/offer-letters')} className="px-4 py-2.5 text-sm font-medium text-[#636E72] hover:bg-stone-100 rounded-xl transition-colors" data-testid="prev-btn">
          {step === 0 ? 'Cancel' : '← Back'}
        </button>
        <div className="flex items-center gap-3">
          {step === 3 && (
            <button onClick={() => handleSave(false)} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 border border-stone-200 rounded-xl text-sm font-medium hover:bg-stone-50 transition-colors" data-testid="save-draft-btn">
              <Save className="w-4 h-4" /> Save as Draft
            </button>
          )}
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext()} className="flex items-center gap-2 px-5 py-2.5 bg-[#D64D7A] hover:bg-[#B52D5B] text-white rounded-xl text-sm font-semibold shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="next-btn">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={() => handleSave(true)} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[#D64D7A] hover:bg-[#B52D5B] text-white rounded-xl text-sm font-semibold shadow-[0_2px_10px_rgba(214,77,122,0.3)] transition-all active:scale-95 disabled:opacity-50" data-testid="send-offer-btn">
              <Send className="w-4 h-4" /> {saving ? 'Sending...' : 'Send Offer to Candidate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STEP 1: CANDIDATE INFO
// ============================================================
function Step1Candidate({ form, set }) {
  return (
    <div className="space-y-5" data-testid="step1-form">
      <h2 className="text-lg font-bold text-[#2D3436] font-[Manrope]">Candidate Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField label="Full Name *" value={form.candidate_name} onChange={v => set('candidate_name', v)} placeholder="e.g. Chinedu Okafor" testId="input-name" />
        <InputField label="Email *" value={form.candidate_email} onChange={v => set('candidate_email', v)} placeholder="e.g. chinedu@gmail.com" type="email" testId="input-email" />
        <InputField label="Phone" value={form.candidate_phone} onChange={v => set('candidate_phone', v)} placeholder="+234..." testId="input-phone" />
        <InputField label="Position/Title *" value={form.position_title} onChange={v => set('position_title', v)} placeholder="e.g. Software Engineer" testId="input-title" />
        <div>
          <label className="block text-sm font-medium text-[#2D3436] mb-1.5">Department</label>
          <select value={form.department} onChange={e => set('department', e.target.value)} className="w-full h-12 rounded-xl border border-stone-200 bg-stone-50 px-4 text-sm focus:bg-white focus:ring-2 focus:ring-[#D64D7A]/20 transition-all" data-testid="input-department">
            <option value="">Select...</option>
            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <InputField label="Reporting To" value={form.reporting_to} onChange={v => set('reporting_to', v)} placeholder="e.g. Head of Engineering" testId="input-reporting" />
        <InputField label="Start Date *" value={form.start_date} onChange={v => set('start_date', v)} type="date" testId="input-start-date" />
        <div>
          <label className="block text-sm font-medium text-[#2D3436] mb-1.5">Employment Type</label>
          <div className="flex gap-4 mt-2">
            {['full_time', 'contract'].map(t => (
              <label key={t} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={form.employment_type === t} onChange={() => set('employment_type', t)} className="w-4 h-4 text-[#D64D7A] focus:ring-[#D64D7A]" />
                <span className="text-sm capitalize">{t.replace('_', '-')}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2D3436] mb-1.5">Probation Period</label>
          <select value={form.probation_months} onChange={e => set('probation_months', +e.target.value)} className="w-full h-12 rounded-xl border border-stone-200 bg-stone-50 px-4 text-sm" data-testid="input-probation">
            {[1,2,3,6].map(m => <option key={m} value={m}>{m} month{m>1?'s':''}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STEP 2: COMPENSATION
// ============================================================
function Step2Compensation({ form, set, tax }) {
  const handleSalaryPreset = (preset) => {
    const presets = {
      standard: { basic_pct: 40, housing_pct: 20, transport_pct: 10, other_pct: 30 },
      custom: form.salary_structure,
    };
    set('salary_preset', preset);
    if (presets[preset]) set('salary_structure', presets[preset]);
  };

  const updateStructure = (key, val) => {
    const s = { ...form.salary_structure, [key]: val };
    set('salary_structure', s);
  };

  return (
    <div className="space-y-6" data-testid="step2-form">
      <h2 className="text-lg font-bold text-[#2D3436] font-[Manrope]">Compensation Package</h2>

      {/* Gross Monthly */}
      <div className="bg-stone-50 rounded-xl p-5">
        <label className="block text-sm font-semibold text-[#2D3436] mb-2">Gross Monthly Salary</label>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-[#636E72]">₦</span>
          <input type="number" value={form.gross_monthly} onChange={e => set('gross_monthly', +e.target.value)} className="flex-1 h-12 rounded-xl border border-stone-200 bg-white px-4 text-lg font-bold font-[Manrope] focus:ring-2 focus:ring-[#D64D7A]/20" data-testid="input-gross" />
        </div>

        {/* Salary Breakdown */}
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-sm font-medium text-[#636E72]">Salary Breakdown:</span>
            <div className="flex gap-2">
              {['standard', 'custom'].map(p => (
                <button key={p} onClick={() => handleSalaryPreset(p)} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${form.salary_preset === p ? 'bg-[#D64D7A] text-white' : 'bg-white border border-stone-200 text-[#636E72]'}`}>
                  {p === 'standard' ? 'Standard 40/20/10/30' : 'Custom'}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[['basic_pct','Basic'],['housing_pct','Housing'],['transport_pct','Transport'],['other_pct','Other']].map(([k,l]) => (
              <div key={k}>
                <p className="text-xs text-[#636E72] mb-1">{l} ({form.salary_structure[k]}%)</p>
                <p className="text-sm font-semibold">{fmt(Math.round(form.gross_monthly * form.salary_structure[k] / 100))}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Budget */}
      <div className="bg-stone-50 rounded-xl p-5">
        <label className="block text-sm font-semibold text-[#2D3436] mb-1">Total Monthly Benefits Budget</label>
        <p className="text-xs text-[#636E72] mb-3">This is the TOTAL amount the company will spend on benefits per month. The candidate will choose how to allocate it.</p>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-[#636E72]">₦</span>
          <input type="number" value={form.benefits_budget} onChange={e => set('benefits_budget', +e.target.value)} className="flex-1 h-12 rounded-xl border border-stone-200 bg-white px-4 text-lg font-bold font-[Manrope] focus:ring-2 focus:ring-[#D64D7A]/20" data-testid="input-benefits" />
        </div>

        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold text-[#636E72] uppercase tracking-wider">Minimum allocations (company policy):</p>
          {[['health','Health (HMO)'],['insurance','Life Insurance']].map(([k,l]) => (
            <div key={k} className="flex items-center gap-3">
              <input type="checkbox" checked={form.min_allocations[k] !== undefined} onChange={e => {
                const m = { ...form.min_allocations };
                if (e.target.checked) m[k] = k === 'health' ? 15000 : 5000;
                else delete m[k];
                set('min_allocations', m);
              }} className="w-4 h-4 text-[#D64D7A] rounded" />
              <span className="text-sm">{l}: min</span>
              <input type="number" value={form.min_allocations[k] || ''} onChange={e => set('min_allocations', { ...form.min_allocations, [k]: +e.target.value })} className="w-28 h-8 rounded-lg border border-stone-200 px-3 text-sm" disabled={!form.min_allocations[k] && form.min_allocations[k] !== 0} />
            </div>
          ))}
        </div>
      </div>

      {/* Employer Pension */}
      <div className="bg-stone-50 rounded-xl p-5">
        <p className="text-sm font-semibold text-[#2D3436]">Employer Contributions (on top of benefits)</p>
        <div className="flex justify-between mt-2 text-sm">
          <span className="text-[#636E72]">Employer Pension (10%)</span>
          <span className="font-semibold">{fmt(tax.monthly_employer_pension)}/month</span>
        </div>
        <p className="text-xs text-[#636E72] mt-1">(Employee pension 8% deducted from gross)</p>
      </div>

      {/* Tax Impact Preview */}
      <div className="bg-gradient-to-br from-pink-50 to-stone-50 rounded-xl p-5 border border-pink-100">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="w-4 h-4 text-[#D64D7A]" />
          <h3 className="text-sm font-bold text-[#2D3436]">Tax Impact Preview</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* WITHOUT */}
          <div className="bg-white/70 rounded-xl p-4">
            <p className="text-xs font-semibold text-[#636E72] uppercase tracking-wider mb-3">Without Pebbles Benefits</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-[#636E72]">Gross salary</span><span>{fmt(form.gross_monthly)}</span></div>
              <div className="flex justify-between"><span className="text-[#636E72]">- Employee pension</span><span className="text-red-500">-{fmt(tax.without.pension)}</span></div>
              <div className="flex justify-between"><span className="text-[#636E72]">- PAYE tax</span><span className="text-red-500">-{fmt(tax.without.paye)}</span></div>
              <div className="flex justify-between pt-2 border-t border-stone-200 font-semibold"><span>Take-home</span><span>{fmt(tax.without.take_home)}</span></div>
              <div className="flex justify-between"><span className="text-[#636E72]">+ Benefits</span><span>₦0</span></div>
              <div className="flex justify-between pt-2 border-t border-stone-200 font-bold text-[#2D3436]"><span>TOTAL VALUE</span><span>{fmt(tax.without.take_home)}</span></div>
            </div>
          </div>
          {/* WITH */}
          <div className="bg-white rounded-xl p-4 border border-pink-100">
            <p className="text-xs font-semibold text-[#D64D7A] uppercase tracking-wider mb-3">With Pebbles Benefits</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-[#636E72]">Gross salary</span><span>{fmt(form.gross_monthly)}</span></div>
              <div className="flex justify-between"><span className="text-[#636E72]">- Benefits (pre-tax)</span><span className="text-[#D64D7A]">-{fmt(form.benefits_budget)}</span></div>
              <div className="flex justify-between"><span className="text-[#636E72]">- Employee pension</span><span className="text-red-500">-{fmt(tax.monthly_employee_pension)}</span></div>
              <div className="flex justify-between"><span className="text-[#636E72]">- PAYE tax</span><span className="text-red-500">-{fmt(tax.monthly_paye)}</span></div>
              <div className="flex justify-between pt-2 border-t border-stone-200 font-semibold"><span>Take-home cash</span><span>{fmt(tax.monthly_take_home)}</span></div>
              <div className="flex justify-between"><span className="text-[#636E72]">+ Benefits value</span><span className="text-[#D64D7A]">+{fmt(form.benefits_budget)}</span></div>
              <div className="flex justify-between pt-2 border-t border-stone-200 font-bold text-[#2D3436]"><span>TOTAL VALUE</span><span>{fmt(tax.monthly_total_value)}</span></div>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg text-emerald-700 font-medium">
            <Check className="w-4 h-4" /> PAYE Reduction: {fmt(tax.tax_saved_monthly)}/month ({fmt(tax.tax_saved_monthly * 12)}/year)
          </div>
        </div>
        <p className="text-[10px] text-[#636E72] mt-3 leading-relaxed">All benefits are fully compliant with Nigerian tax law (NTA 2025). Company-provided benefits delivered through approved vendors are tax-exempt under the Personal Income Tax Act.</p>
      </div>
    </div>
  );
}

// ============================================================
// STEP 3: OFFER SETTINGS
// ============================================================
function Step3Settings({ form, set }) {
  return (
    <div className="space-y-6" data-testid="step3-form">
      <h2 className="text-lg font-bold text-[#2D3436] font-[Manrope]">Offer Settings</h2>

      {/* Selection Mode */}
      <div className="bg-stone-50 rounded-xl p-5">
        <p className="text-sm font-semibold text-[#2D3436] mb-3">Benefits Selection Mode</p>
        {[
          { value: 'candidate_chooses', title: 'Candidate chooses (recommended)', desc: 'Candidate receives a link to select their own benefits allocation before the final offer letter is generated.' },
          { value: 'company_assigns', title: 'Company pre-assigns', desc: 'Admin assigns benefits. Candidate receives the final offer letter directly.' },
        ].map(m => (
          <label key={m.value} className={`flex items-start gap-3 p-4 rounded-xl mb-2 cursor-pointer transition-colors ${form.selection_mode === m.value ? 'bg-pink-50 border border-pink-200' : 'bg-white border border-stone-100'}`}>
            <input type="radio" checked={form.selection_mode === m.value} onChange={() => set('selection_mode', m.value)} className="w-4 h-4 mt-0.5 text-[#D64D7A]" />
            <div>
              <p className="text-sm font-semibold text-[#2D3436]">{m.title}</p>
              <p className="text-xs text-[#636E72] mt-0.5">{m.desc}</p>
            </div>
          </label>
        ))}
      </div>

      {/* Enabled Categories */}
      <div className="bg-stone-50 rounded-xl p-5">
        <p className="text-sm font-semibold text-[#2D3436] mb-3">Enabled Benefit Categories</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {CATEGORIES.map(c => {
            const minNote = form.min_allocations[c.slug] ? ` (min ${fmt(form.min_allocations[c.slug])})` : '';
            return (
              <label key={c.slug} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-stone-100 cursor-pointer hover:border-pink-200 transition-colors">
                <input type="checkbox" checked={form.enabled_categories.includes(c.slug)} onChange={e => {
                  const cats = e.target.checked ? [...form.enabled_categories, c.slug] : form.enabled_categories.filter(x => x !== c.slug);
                  set('enabled_categories', cats);
                }} className="w-4 h-4 text-[#D64D7A] rounded" />
                <span className="text-sm">{c.icon} {c.label}{minNote}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Additional Terms */}
      <div className="bg-stone-50 rounded-xl p-5">
        <p className="text-sm font-semibold text-[#2D3436] mb-3">Additional Offer Terms</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <SelectField label="Annual Leave" value={form.annual_leave_days} onChange={v => set('annual_leave_days', +v)} options={[15,20,25,30].map(n => ({ value: n, label: `${n} days` }))} testId="input-leave" />
          <SelectField label="Sick Leave" value={form.sick_leave_days} onChange={v => set('sick_leave_days', +v)} options={[5,7,10,15].map(n => ({ value: n, label: `${n} days` }))} testId="input-sick" />
          <SelectField label="Work Arrangement" value={form.work_arrangement} onChange={v => set('work_arrangement', v)} options={['hybrid','remote','onsite'].map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }))} testId="input-work" />
          <SelectField label="Notice Period" value={form.notice_period} onChange={v => set('notice_period', v)} options={['2 weeks','1 month','2 months','3 months'].map(v => ({ value: v, label: v }))} testId="input-notice" />
          <SelectField label="Offer Expiry" value={form.offer_expiry_days} onChange={v => set('offer_expiry_days', +v)} options={[3,5,7,14,30].map(n => ({ value: n, label: `${n} days from sent` }))} testId="input-expiry" />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-[#2D3436] mb-1.5">Signing Bonus</label>
            <div className="flex items-center gap-1"><span className="text-sm text-[#636E72]">₦</span><input type="number" value={form.signing_bonus} onChange={e => set('signing_bonus', +e.target.value)} className="flex-1 h-10 rounded-lg border border-stone-200 px-3 text-sm" /></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2D3436] mb-1.5">Relocation Support</label>
            <div className="flex items-center gap-1"><span className="text-sm text-[#636E72]">₦</span><input type="number" value={form.relocation_support} onChange={e => set('relocation_support', +e.target.value)} className="flex-1 h-10 rounded-lg border border-stone-200 px-3 text-sm" /></div>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-[#2D3436] mb-1.5">Additional Notes to Candidate</label>
          <textarea value={form.additional_notes} onChange={e => set('additional_notes', e.target.value)} rows={3} className="w-full rounded-xl border border-stone-200 bg-white p-3 text-sm focus:ring-2 focus:ring-[#D64D7A]/20" placeholder="Any additional information for the candidate..." data-testid="input-notes" />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STEP 4: PREVIEW & SEND
// ============================================================
function Step4Preview({ form, tax }) {
  const totalPkg = form.gross_monthly + form.benefits_budget;
  return (
    <div className="space-y-5" data-testid="step4-preview">
      <h2 className="text-lg font-bold text-[#2D3436] font-[Manrope]">Review & Send</h2>

      <div className="bg-stone-50 rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-[#636E72] uppercase tracking-wider">Summary</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-[#636E72]">Candidate:</span> <span className="font-medium">{form.candidate_name}</span></div>
          <div><span className="text-[#636E72]">Position:</span> <span className="font-medium">{form.position_title}</span></div>
          <div><span className="text-[#636E72]">Department:</span> <span className="font-medium">{form.department || '-'}</span></div>
          <div><span className="text-[#636E72]">Start Date:</span> <span className="font-medium">{form.start_date}</span></div>
          <div><span className="text-[#636E72]">Gross Salary:</span> <span className="font-semibold">{fmt(form.gross_monthly)}/month</span></div>
          <div><span className="text-[#636E72]">Benefits:</span> <span className="font-semibold text-[#D64D7A]">{fmt(form.benefits_budget)}/month</span></div>
          <div><span className="text-[#636E72]">Total Package:</span> <span className="font-bold">{fmt(totalPkg)}/month</span></div>
          <div><span className="text-[#636E72]">Offer Expiry:</span> <span className="font-medium">{form.offer_expiry_days} days from sent</span></div>
        </div>
      </div>

      <div className="bg-pink-50 rounded-xl p-5 border border-pink-100">
        <p className="text-sm text-[#2D3436]">
          The candidate will receive an email with a link to:
        </p>
        <ol className="mt-2 space-y-1 text-sm text-[#636E72] list-decimal pl-5">
          <li>View their full compensation breakdown</li>
          <li>See the tax impact of benefits</li>
          <li>Browse all available vendors</li>
          <li>Choose their benefits allocation</li>
          <li>Accept and sign the offer</li>
          <li>Download the final offer letter PDF</li>
        </ol>
      </div>

      <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-emerald-600 font-semibold uppercase">Monthly PAYE</p>
            <p className="text-lg font-bold text-emerald-700 font-[Manrope]">{fmt(tax.monthly_paye)}</p>
          </div>
          <div>
            <p className="text-xs text-emerald-600 font-semibold uppercase">Tax Saved</p>
            <p className="text-lg font-bold text-emerald-700 font-[Manrope]">{fmt(tax.tax_saved_monthly)}/mo</p>
          </div>
          <div>
            <p className="text-xs text-emerald-600 font-semibold uppercase">Total Value</p>
            <p className="text-lg font-bold text-emerald-700 font-[Manrope]">{fmt(tax.monthly_total_value)}/mo</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// REUSABLE COMPONENTS
// ============================================================
function InputField({ label, value, onChange, placeholder, type = 'text', testId }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#2D3436] mb-1.5">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full h-12 rounded-xl border border-stone-200 bg-stone-50 px-4 text-sm focus:bg-white focus:ring-2 focus:ring-[#D64D7A]/20 transition-all font-medium" data-testid={testId} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#2D3436] mb-1.5">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full h-10 rounded-lg border border-stone-200 bg-white px-3 text-sm" data-testid={testId}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
